import React, { useState, useEffect } from 'react';
import { Info, ToggleLeft, ToggleRight } from 'lucide-react';
import businessService from '../../services/businessService';

const ZONES = [
  {
    num: 1,
    name: 'Local',
    description: 'Baseline — always active',
    gradient: 'linear-gradient(135deg, #0f172a, #1e293b)',
    badgeClass: 'bg-slate-600 text-slate-200',
    toggleable: false,
    offGradient: 'linear-gradient(135deg, #0f172a, #1e293b)',
    adjustmentClass: 'text-slate-300',
    toggleOnClass: 'text-slate-300',
    previewClass: 'text-white',
    previewBadgeClass: 'bg-slate-600 text-slate-200',
    labelClass: 'text-slate-300',
    descClass: 'text-slate-400',
    rangeClass: 'bg-slate-700 border-slate-600 text-slate-300',
    inputClass: 'bg-slate-700 border-slate-600 text-white placeholder-slate-400',
    unitClass: 'text-slate-400',
    feedbackClass: 'text-slate-300',
  },
  {
    num: 2,
    name: 'Regional',
    description: 'Enable to activate',
    gradient: 'linear-gradient(135deg, #422006, #78350f)',
    offGradient: 'linear-gradient(135deg, #1c1917, #292524)',
    badgeClass: 'bg-amber-800 text-amber-200',
    toggleable: true,
    adjustmentClass: 'text-amber-300',
    toggleOnClass: 'text-amber-300',
    previewClass: 'text-amber-300',
    previewBadgeClass: 'bg-amber-900 text-amber-300',
    labelClass: 'text-amber-200',
    descClass: 'text-amber-400',
    rangeClass: 'bg-amber-900 border-amber-700 text-amber-200',
    inputClass: 'bg-amber-900 border-amber-700 text-white placeholder-amber-600',
    unitClass: 'text-amber-400',
    feedbackClass: 'text-amber-300',
  },
  {
    num: 3,
    name: 'Extended',
    description: 'Beyond Regional',
    gradient: 'linear-gradient(135deg, #450a0a, #7f1d1d)',
    offGradient: 'linear-gradient(135deg, #1c1917, #292524)',
    badgeClass: 'bg-red-900 text-red-200',
    toggleable: true,
    adjustmentClass: 'text-red-300',
    toggleOnClass: 'text-red-300',
    previewClass: 'text-red-300',
    previewBadgeClass: 'bg-red-900 text-red-300',
    labelClass: 'text-red-200',
    descClass: 'text-red-400',
    rangeClass: 'bg-red-900 border-red-700 text-red-200',
    inputClass: 'bg-red-900 border-red-700 text-white placeholder-red-700',
    unitClass: 'text-red-400',
    feedbackClass: 'text-red-300',
  },
];

// Convert stored multiplier (1.1) → display percent (10)
const multiplierToPercent = (multiplier) => {
  const m = parseFloat(multiplier) || 1;
  return Math.round((m - 1) * 10000) / 100;
};

// Convert display percent (10) → stored multiplier (1.1)
const percentToMultiplier = (percent) => {
  const p = parseFloat(percent) || 0;
  return Math.round((1 + p / 100) * 10000) / 10000;
};

const applyPercent = (amount, percent) => {
  const p = parseFloat(percent) || 0;
  const result = amount * (1 + p / 100);
  return result % 1 === 0 ? result : parseFloat(result.toFixed(2));
};

const RouteSettingsTab = ({ showToast }) => {
  const [routeSettings, setRouteSettings] = useState({
    defaultServiceDurationMinutes: 60,
    averageDriveSpeedMph: 35,
    routeStartTime: '08:00',
    routeEndTime: '17:00',
    optimizationPriority: 'Balanced',
    distanceZone1Enabled: true,
    distanceZone1Miles: 10,
    distanceZone1Multiplier: 1.0,
    distanceZone2Enabled: false,
    distanceZone2Miles: 20,
    distanceZone2Multiplier: 1.1,
    distanceZone3Enabled: false,
    distanceZone3Miles: 30,
    distanceZone3Multiplier: 1.2,
  });

  const [zonePercents, setZonePercents] = useState({ 1: 0, 2: 10, 3: 20 });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showZoneInfo, setShowZoneInfo] = useState(false);

  useEffect(() => {
    loadRouteSettings();
  }, []);

  const loadRouteSettings = async () => {
    try {
      const response = await businessService.getRouteSettings();
      const data = response.data;
      setRouteSettings(data);
      setZonePercents({
        1: multiplierToPercent(data.distanceZone1Multiplier),
        2: multiplierToPercent(data.distanceZone2Multiplier),
        3: multiplierToPercent(data.distanceZone3Multiplier),
      });
    } catch (error) {
      console.error('Error loading route settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        ...routeSettings,
        distanceZone1Multiplier: percentToMultiplier(zonePercents[1]),
        distanceZone2Multiplier: percentToMultiplier(zonePercents[2]),
        distanceZone3Multiplier: percentToMultiplier(zonePercents[3]),
      };
      await businessService.updateRouteSettings(payload);
      showToast('Route settings saved successfully!');
    } catch (error) {
      console.error('Error saving route settings:', error);
      showToast(error.response?.data?.error || 'Failed to save route settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateZoneField = (zone, field, value) => {
    setRouteSettings(prev => ({
      ...prev,
      [`distanceZone${zone}${field}`]: value,
    }));
  };

  const updateZonePercent = (zone, value) => {
    setZonePercents(prev => ({ ...prev, [zone]: value }));
  };

  const getZoneRangeLabel = (zone) => {
    const z1 = parseInt(routeSettings.distanceZone1Miles) || 10;
    const z2 = parseInt(routeSettings.distanceZone2Miles) || 20;
    if (zone === 1) return `0 – ${z1} mi`;
    if (zone === 2) return `${z1 + 1} – ${z2} mi`;
    if (zone === 3) return `${z2 + 1}+ mi`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="card animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const zone2On = routeSettings.distanceZone2Enabled;
  const zone3On = routeSettings.distanceZone3Enabled;

  return (
    <div className="space-y-6">

      {/* ── Route Optimization Settings ── */}
      <div className="card">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Route Optimization Settings</h3>
          <p className="text-sm text-gray-600">Configure how SuddyRoute™ optimizes your daily routes</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Service Duration</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={routeSettings.defaultServiceDurationMinutes}
                onClick={(e) => e.target.select()}
                onChange={(e) => setRouteSettings({ ...routeSettings, defaultServiceDurationMinutes: parseInt(e.target.value) })}
                className="input flex-1"
                min="15"
                max="480"
              />
              <span className="text-sm text-gray-600 whitespace-nowrap">minutes</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Used when job duration isn't specified</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Average Drive Speed</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={routeSettings.averageDriveSpeedMph}
                onClick={(e) => e.target.select()}
                onChange={(e) => setRouteSettings({ ...routeSettings, averageDriveSpeedMph: parseInt(e.target.value) })}
                className="input flex-1"
                min="15"
                max="70"
              />
              <span className="text-sm text-gray-600 whitespace-nowrap">mph</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">For calculating drive times between jobs</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Business Start Time</label>
            <input
              type="time"
              value={routeSettings.routeStartTime}
              onChange={(e) => setRouteSettings({ ...routeSettings, routeStartTime: e.target.value })}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">When your workday begins</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Business End Time</label>
            <input
              type="time"
              value={routeSettings.routeEndTime}
              onChange={(e) => setRouteSettings({ ...routeSettings, routeEndTime: e.target.value })}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">When your workday ends</p>
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Optimization Priority</label>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { value: 'Revenue', emoji: '💰', label: 'Revenue First', desc: 'Maximize revenue per hour' },
              { value: 'Balanced', emoji: '⚖️', label: 'Balanced', desc: 'Best mix (recommended)' },
              { value: 'Distance', emoji: '🛣️', label: 'Shortest Route', desc: 'Minimize miles driven' },
            ].map(({ value, emoji, label, desc }) => (
              <button
                key={value}
                type="button"
                onClick={() => setRouteSettings({ ...routeSettings, optimizationPriority: value })}
                className={`p-4 border-2 rounded-lg transition-all ${
                  routeSettings.optimizationPriority === value
                    ? value === 'Revenue' ? 'border-green-500 bg-green-50'
                      : value === 'Balanced' ? 'border-blue-500 bg-blue-50'
                      : 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-2xl mb-2">{emoji}</div>
                <div className="font-semibold text-gray-900">{label}</div>
                <div className="text-xs text-gray-600 mt-1">{desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Distance Zone Pricing ── */}
      <div className="card">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Distance Zone Pricing</h3>
          <p className="text-sm text-gray-600">
            Automatically add a percentage to quotes based on how far the job is from your business.
            The adjustment is built into the price — no travel fee shown to the customer.
          </p>
        </div>

        <div className="mb-6">
  <button
    type="button"
    onClick={() => setShowZoneInfo(prev => !prev)}
    className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium"
  >
    <Info size={14} />
    How does this work?
  </button>
  {showZoneInfo && (
    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
      <p className="text-sm text-blue-800">
        Set a max mile radius for <strong>Local</strong> and <strong>Regional</strong> — anything beyond Regional is automatically <strong>Extended</strong>.
        Enter the <strong>% to add</strong> for each zone. <strong>0</strong> means no change,
        <strong> 10</strong> adds 10%, <strong>2.5</strong> adds 2.5%.
        Regional and Extended must be toggled on to apply.
      </p>
    </div>
  )}
</div>

        <div className="space-y-3">
          {ZONES.map((zone) => {
            const enabled = zone.num === 1 ? true : routeSettings[`distanceZone${zone.num}Enabled`];
            const miles = routeSettings[`distanceZone${zone.num}Miles`];
            const pct = zonePercents[zone.num];
            const gradient = enabled ? zone.gradient : zone.offGradient;

            return (
              <div
                key={zone.num}
                className="rounded-xl p-4 transition-all"
                style={{ background: gradient }}
              >
                <div className="flex items-start gap-3">
                  {/* Zone number badge */}
                  <span className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5 ${zone.badgeClass}`}>
                    {zone.num}
                  </span>

                  <div className="flex-1 min-w-0">
                    {/* Name + toggle + range */}
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={`font-semibold ${zone.labelClass}`}>{zone.name}</p>
                          {zone.toggleable && (
                            <button
                              type="button"
                              onClick={() => {
  const turningOff = enabled;
  updateZoneField(zone.num, 'Enabled', !enabled);
  if (turningOff) {
    updateZoneField(zone.num, 'Miles', zone.num === 2 ? 20 : 30);
    updateZonePercent(zone.num, 0);
  }
}}
                              className={`flex items-center gap-1 text-xs font-medium ${zone.toggleOnClass}`}
                            >
                              {enabled
                                ? <><ToggleRight size={18} /><span>On</span></>
                                : <><ToggleLeft size={18} className="text-gray-500" /><span className="text-gray-500">Off</span></>
                              }
                            </button>
                          )}
                        </div>
                        <p className={`text-xs ${zone.descClass}`}>{zone.description}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded border whitespace-nowrap flex-shrink-0 ${zone.rangeClass}`}>
                        {getZoneRangeLabel(zone.num)}
                      </span>
                    </div>

                    {/* Inputs */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={`block text-xs mb-1 ${zone.labelClass}`}>
                          {zone.num === 3 ? 'Reference miles' : 'Max miles'}
                        </label>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            min="1"
                            max="200"
                            value={zone.toggleable && !enabled ? '' : miles}
                            onClick={(e) => e.target.select()}
                            onChange={(e) => {
                              const val = e.target.value;
                              updateZoneField(zone.num, 'Miles', val === '' ? '' : parseInt(val));
                            }}
                            disabled={zone.toggleable && !enabled}
                            className={`rounded-lg border px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-white/20 disabled:opacity-40 disabled:cursor-not-allowed ${zone.inputClass}`}
                          />
                          <span className={`text-xs flex-shrink-0 ${zone.unitClass}`}>mi</span>
                        </div>
                        {zone.num === 3 && (
                          <p className={`text-xs mt-1 ${zone.descClass}`}>No upper limit</p>
                        )}
                      </div>

                      <div>
                        <label className={`block text-xs mb-1 ${zone.labelClass}`}>% Added to quote</label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={zone.toggleable && !enabled ? '' : (pct === 0 || pct === '0' ? '0' : pct || '')}
                            onClick={(e) => e.target.select()}
                            onChange={(e) => updateZonePercent(zone.num, e.target.value)}
                            disabled={zone.toggleable && !enabled}
                            className={`rounded-lg border px-3 py-2 pr-8 text-sm w-full focus:outline-none focus:ring-2 focus:ring-white/20 disabled:opacity-40 disabled:cursor-not-allowed ${zone.inputClass}`}
                            placeholder="0"
                          />
                          <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none ${zone.unitClass}`}>%</span>
                        </div>
                        {enabled && (
                          <p className={`text-xs mt-1 font-medium ${parseFloat(pct) > 0 ? zone.feedbackClass : zone.descClass}`}>
                            {parseFloat(pct) > 0
                              ? `+${pct}% · e.g. $300 → $${applyPercent(300, pct) % 1 === 0 ? applyPercent(300, pct) : applyPercent(300, pct).toFixed(2)}`
                              : 'No adjustment'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Live preview */}
        {(zone2On || zone3On) && (
          <div className="mt-5 p-4 bg-gray-900 rounded-xl">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Preview — what a $300 quote becomes per zone</p>
            <div className="space-y-2">
              {ZONES.map((zone) => {
                const enabled = zone.num === 1 ? true : routeSettings[`distanceZone${zone.num}Enabled`];
                if (!enabled) return null;
                const pct = zonePercents[zone.num];
                const result = applyPercent(300, pct);
                const added = parseFloat((result - 300).toFixed(2));
                return (
                  <div key={zone.num} className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-200 font-medium">{zone.name}</span>
                      <span className="text-xs text-gray-500">{getZoneRangeLabel(zone.num)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {added > 0 && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${zone.previewBadgeClass}`}>
                          +${added % 1 === 0 ? added : added.toFixed(2)}
                        </span>
                      )}
                      <span className={`font-semibold ${zone.previewClass}`}>
                        ${result % 1 === 0 ? result : result.toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              * Requires your business address to have coordinates set in Business Info → Refresh Location
            </p>
          </div>
        )}
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving} className="btn btn-primary">
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

    </div>
  );
};

export default RouteSettingsTab;